import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { useAdjustSalary } from "./adjustSalary.hook";

type Props = {
  companyId: number;
  onClose: () => void;
  onSaved: () => void;
  open: boolean;
  salaryNoticeId: number;
  workerId: number;
  workerName: string;
};

export const AdjustSalaryModal: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const { save, status } = useAdjustSalary(props.companyId);

  const numericValue = parseFloat(value);
  const isValid = value !== "" && !isNaN(numericValue) && numericValue !== 0;

  const isSubmitting = status.type === "submitting";
  const isSuccess = status.type === "success";
  const errorMessage = status.type === "error" ? status.message : null;

  const handleSave = (): void => {
    if (!isValid || isSubmitting) return;
    save({
      workerId: props.workerId,
      salaryNoticeId: props.salaryNoticeId,
      value: numericValue,
    });
  };

  if (isSuccess) {
    return (
      <Modal
        isOpen={props.open}
        onClose={props.onSaved}
        label={t("Salary.Changed")}
        size="compact"
        footer={
          <PrimaryButton
            label={t("OrganizationalStructure.Ok")}
            onClick={props.onSaved}
            size="small"
          />
        }
      >
        <Text typography="b2" color={ColorV2.Blue1}>
          {t("Salary.SalaryAdjusted")}
        </Text>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={props.open}
      onClose={props.onClose}
      label={t("Salary.AdjustSalary")}
      size="compact"
      footer={
        <>
          <SecondaryButton
            label={t("Salary.Cancel")}
            onClick={props.onClose}
            size="small"
            disabled={isSubmitting}
          />
          <PrimaryButton
            label={t("Salary.Save")}
            disabled={!isValid || isSubmitting}
            onClick={handleSave}
            size="small"
          />
        </>
      }
    >
      <Text typography="h5" color={ColorV2.Blue3}>
        {props.workerName}
      </Text>

      {errorMessage && (
        <Text typography="s1" color={ColorV2.NegativeRed}>
          {errorMessage}
        </Text>
      )}

      <InputField
        label={t("Salary.ExtraPay")}
        type="number"
        value={value}
        onChange={setValue}
        placeholder={t("Salary.ExtraPayPlaceholder")}
        fullWidth
      />

      <Text typography="s1" color={ColorV2.Blue4}>
        {t("Salary.ExtraPayHelperText")}
      </Text>
    </Modal>
  );
};
